import {renderToStaticMarkup} from "react-dom/server";
import React, {ReactElement, ReactNode} from "react";
import {
    DeserializedElement,
    DeserializedElementRule,
    DeserializedLeaf, DeserializedLeafRule, DeserializedNode,
    DeserializedRules, RenderElement
} from "../Transformation";
import {Editor, Element, Element as SlateElement, Node, NodeEntry, Path, Range, Text, Transforms} from "slate";
import {ReactComponentLike} from "prop-types";
import {hotKeyAction, HotKeyType} from "../utils/hotkey";
import urldecode from "locutus/php/url/urldecode";
import strip_tags from "locutus/php/strings/strip_tags";

/**
 * Gets the name of the generator used to create the given content.
 *
 * It returns `undefined` if the `<meta>` tag with the generator name was not present.
 * It returns `'unknown'` if the `<meta>` tag has a generator other than `'microsoft'` or `'libreoffice'`.
 *
 * @param {String} content The clipboard data.
 * @returns {String/undefined} The name of the recognized content generator. Possible values: `'microsoft'`, `'libreoffice'`, `'unknown'`, `undefiend`.
 */
const getContentGeneratorName = function( content: string ) {
    let metaGeneratorTag = /<meta\s+name=["']?generator["']?\s+content=["']?([\s\w\d]+)/gi,
        result = metaGeneratorTag.exec( content );

    if ( !result || !result.length ) {
        return 'unknown';
    }

    return result?.[1]?.toLowerCase() || 'unknown';
}

const isOfficeWord = function (data: DataTransfer) {
    let
    mswordHtml = data.getData('text/html'),
    generatorName = getContentGeneratorName( mswordHtml ),
    //wordRegexp = /(class="?Mso|style=["'][^"]*?\bmso\-|w:WordDocument|<o:\w+>|<\/font>)/,
    // Use wordRegexp only when there is no meta generator tag in the content
    isOfficeContent = generatorName.indexOf('microsoft') === 0 && generatorName.indexOf('word') !== -1;//wordRegexp.test( mswordHtml );

    return mswordHtml && isOfficeContent;
}

const isOfficeExcel = function (data: DataTransfer) {
    let
    msexcelHtml = data.getData('text/html'),
    generatorName = getContentGeneratorName( msexcelHtml ),
    //excelRegexp = /(Excel\.Sheet)/,
    // Use wordRegexp only when there is no meta generator tag in the content
    isOfficeContent = generatorName.indexOf('microsoft') === 0 && generatorName.indexOf('excel') !== -1;//generatorName ? generatorName === 'microsoft' : excelRegexp.test( msexcelHtml );

    return msexcelHtml && isOfficeContent;
}

/*
<html>
<body>
<!--StartFragment-->
    <span data-slate-node="element" data-slate-inline="true" data-slate-fragment="JTVCJTdCJTIydHlwZSUyMiUzQSUyMnAlMjIlMkMlMjJwcm9wcyUyMiUzQSU3QiU3RCUyQyUyMmF0dHIlMjIlM0ElN0IlN0QlMkMlMjJjaGlsZHJlbiUyMiUzQSU1QiU3QiUyMnR5cGUlMjIlM0ElMjJzcGFuJTIyJTJDJTIycHJvcHMlMjIlM0ElN0IlN0QlMkMlMjJhdHRyJTIyJTNBJTdCJTdEJTJDJTIyY2hpbGRyZW4lMjIlM0ElNUIlN0IlMjJ0ZXh0JTIyJTNBJTIyb25saW5lJTIyJTdEJTVEJTdEJTJDJTdCJTIydHlwZSUyMiUzQSUyMnNwYW4lMjIlMkMlMjJwcm9wcyUyMiUzQSU3QiU3RCUyQyUyMmF0dHIlMjIlM0ElN0IlN0QlMkMlMjJjaGlsZHJlbiUyMiUzQSU1QiU3QiUyMnRleHQlMjIlM0ElMjIlMjIlN0QlNUQlN0QlMkMlN0IlMjJ0eXBlJTIyJTNBJTIyc3BhbiUyMiUyQyUyMnByb3BzJTIyJTNBJTdCJTdEJTJDJTIyYXR0ciUyMiUzQSU3QiU3RCUyQyUyMmNoaWxkcmVuJTIyJTNBJTVCJTdCJTIydGV4dCUyMiUzQSUyMiUyMiU3RCU1RCU3RCU1RCU3RCU1RA==">
        <span data-slate-node="text">
            <span data-slate-leaf="true">
                <span data-slate-string="true">online</span>
            </span>
        </span>
    </span>
    <span data-slate-node="element" data-slate-inline="true">
        <span data-slate-node="text">
            <span data-slate-leaf="true">
                <span data-slate-zero-width="z" data-slate-length="0"></span>
            </span>
        </span>
    </span>
    <span data-slate-node="element" data-slate-inline="true">
        <span data-slate-node="text">
            <span data-slate-leaf="true">
                <span data-slate-zero-width="z" data-slate-length="0"></span>
            </span>
        </span>
    </span>
<!--EndFragment-->
</body>
</html>
 */
const catchSlateFragment = /data-slate-fragment="(.+?)"/m
const isSlate = function (data: DataTransfer) {
    const htmlData = data.getData('text/html')
    const [, fragment] = htmlData.match(catchSlateFragment) || []
    return fragment;
}

// https://stackoverflow.com/a/58531877/1149311
const CleanWordFormatting = (input: string): string => {

    let output = strip_tags(input, '<p><b><i><s><a><u><br><br/><ul><li><ol><h1><h2><h3><h4><h5><h6><h7><h8><h9><h10>').trim();

    output = output.replace(/<br>/g, '<br/>');

    //output = output.replace(/( [\n\r]+)|([\n\r]+ )/g, ' ');
    output = output.replace(/([\n\r]*<br\/>[\n\r]*)/g, '<br/>');
    output = output.replace(/([\n\r]+)/g, ' ');

    // 1. Remove line breaks / Mso classes
    let stringStripper = /(class=(")?Mso[a-zA-Z]+(")?)/g;
    output = output.replace(stringStripper, ' ');

    // 2. Strip Word generated HTML comments
    let commentSripper = new RegExp('<!--(.*?)-->', 'g');
    output = output.replace(commentSripper, '');
    let tagStripper = new RegExp('<(/)*(meta|link|\\?xml:|st1:|o:|font)(.*?)>', 'gi');

    // 3. Remove tags leave content if any
    output = output.replace(tagStripper, '');

    // 4. Remove everything in between and including tags '<style(.)style(.)>'
    let badTags = ['style', 'script', 'applet', 'embed', 'noframes', 'noscript'];
    for (let i = 0; i < badTags.length; i++) {
        tagStripper = new RegExp('<' + badTags[i] + '.*?' + badTags[i] + '(.*?)>', 'gi');
        output = output.replace(tagStripper, '');
    }

    // 5. Remove any unwanted styling
    // NOTE: Add your own list of 'blacklisted' css attributes here
    let badStyling = ['margin-top:', 'margin-bottom:', 'line-height:', 'mso-fareast-font-family:&quot;', 'font-weight:', 'margin:', 'mso-\\w+-font-family:'];
    for (let i = 0; i < badStyling.length; i++) {
        let attrStripper = new RegExp('(' + badStyling[i] + ')([^;]*)+[^]', 'gm');
        output = output.replace(attrStripper, '');
    }

    // 6. Remove any unwanted attributes
    let badAttributes = ['start'];
    for (let i = 0; i < badAttributes.length; i++) {
        let attributeStripper = new RegExp(' ' + badAttributes[i] + '="(.*?)"', 'gi');
        output = output.replace(attributeStripper, '');
    }

    return output.trim();
}

const htmlserializer = {
    deserialize: (data: string|DataTransfer, rules: DeserializedRules<HTMLElement>) => {
        /*const recursive = (node) => {
            let obj: DeserializedElement = {};
            if (node.nodeType === Node.TEXT_NODE) {
                obj.text = node.textContent;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                obj.type = node.tagName;
                obj.children = [];
                node.childNodes.forEach((child) => {
                    obj.children.push(recursive(child));
                });
                if (!obj.children.length) {
                    obj.children = [{text: ''}];
                }
            }
            //console.log(obj);
            return obj;
        }
        let dom = new DOMParser().parseFromString("<document>" + data + "</document>", 'text/xml')
        //window.temp1 = dom;
        //console.log(dom);
        let json = recursive(dom.childNodes[0]);
        //console.log(json);
        return json.children;*/
        const normalize = (nodes: Node[]): DeserializedNode|DeserializedNode[]|null|undefined => {
            return nodes.map((o) => {
                // normalize json dom for slate
                if (Element.isElement(o)) {
                    if (o.children?.length > 1) {
                        o.children = normalize(o.children.filter((leaf) => !(Text.isText(leaf) && leaf.text === '')));
                    } else {
                        o.children = normalize(o.children);
                    }
                }
                if (Element.isElement(o) && !o.children?.length) {
                    o.children = [{text: ''}];
                }/* else if (Text.isText(o)) {
                    o.text = o.text.trim();
                }*/
                return o;
            });
        }
        const build = (el: ChildNode, rules: DeserializedRules<HTMLElement>): DeserializedElement|DeserializedElement[] => {
            let allRules = [...Object.values(rules.elements), ...Object.values(rules.leaves)];
            //console.log(el);
            for (let i = 0; i < allRules.length; i++) {
                let obj = allRules[i].deserialize(el as HTMLElement, children => Array.from(children || []).reduce((all, child) => {
                    let next = build(child, rules);
                    if (!Array.isArray(next)) {
                        next = [next];
                    }
                    all = [...all, ...next].filter((node) => !!node);
                    return all;
                }, []));
                if (obj !== undefined) {
                    if (!Array.isArray(obj)) {
                        obj = [obj];
                    }
                    //console.log(JSON.parse(JSON.stringify(obj)));
                    obj = normalize(obj);
                    //console.log(JSON.parse(JSON.stringify(obj)));
                    return obj;
                }
            }
            return null;
        }
        if (data instanceof DataTransfer) {
            //console.log(data.getData('text/html'));
            let isSlateData: string;
            //console.log(data.getData("text/html"));
            if (isOfficeWord(data)) {
                //console.log(data);
                data = data.getData('text/html');
                //console.log(data);
                data = CleanWordFormatting(data);
                //console.log(data);
                data = new DOMParser().parseFromString(data, 'text/html').querySelector("body").innerHTML.trim();
                if (data.indexOf("<!--StartFragment-->") === 0) {
                    data = data.slice("<!--StartFragment-->".length, 0 - ("<!--EndFragment-->".length)).trim();
                }
                data = data.replace(/<br>/g, '<br/>');
                //console.log(data);
            } else if (isOfficeExcel(data)) {
                // TODO
                data = data.getData('text/plain').replace(/(\n\r)|(\r\n)|(\n)|(\r)/gi, "<br/>");
            } else if (isSlateData = isSlate(data)) {
                let slate = normalize(JSON.parse(urldecode(atob(isSlateData))));
                //console.log(JSON.parse(JSON.stringify(slate)));
                return Array.isArray(slate) ? slate : [slate];
            } else {
                // TODO
                /*data = data.getData('text/html');
                //console.log(data);
                data = new DOMParser().parseFromString(data, 'text/html').querySelector("body").innerHTML.trim();
                if (data.indexOf("<!--StartFragment-->") === 0) {
                    data = data.slice("<!--StartFragment-->".length, 0 - ("<!--EndFragment-->".length)).trim();
                }
                //console.log(data);*/
                data = data.getData('text/plain')
                .replace(/&(?!amp;)/g, '&amp;')
                .replace(/>/g, '&gt;')
                .replace(/</g, '&lt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&apos;')
                .replace(/(\n\r)|(\r\n)|(\n)|(\r)/gi, "<br/>");
            }
        }
        data = (data || "<p />")//.replace(/([a-zA-Z_\-]\/?>)\s+(<\/?[a-zA-Z_])/g, '$1$2')/*.replace(/\>[\s]+\</g, "><")//.replace(/[\s|>]\s*(<\/?[a-zA-Z_\-])/g, '>$1').replace(/([a-zA-Z_\-]\/?>)\s+([^\s])/g, '$1$2')/*.replace(/\>[\s]+\</g, "><")*/;
        let dom = new DOMParser().parseFromString("<document>" + data + "</document>", 'text/xml')
        let array = Array.from(dom.childNodes[0].childNodes).reduce((all, el) => {
            let next = build(el, rules);
            if (!Array.isArray(next)) {
                next = [next];
            }
            all = [...all, ...next].filter((node) => !!node).filter((node) => !Text.isText(node) || node.text !== '')/*.map((node) => {
                if (Text.isText(node)) {
                    return {type: "p", children: [node]};
                } else {
                    return node;
                }
            }).reduce((all, current) => {
                if (all[all.length-1]?.type === "p" && current.type === "br") {
                    all[all.length-1].children = [...all[all.length-1].children, current];
                    return all;
                }
                return [...all, current];
            }, []);*/
            return all;
        }, []);
        //console.log(JSON.parse(JSON.stringify(array)));
        return array;
    },
    serialize: (node: DeserializedElement[], rules: DeserializedRules<HTMLElement>) => {
        /*const recursive = (node) => {
            if (Text.isText(node)) {
                //console.log(node);
                let string = escapeHtml(node.text)
                if (node.styles) {
                    node.styles.map((style) => {
                        string = `<${style}>${string}</${style}>`;
                    })
                }
                //console.log(string);
                return string
            }

            const children = node.children?.map(n => recursive(n)).join('')

            if (node.type) {
                return `<${node.type}>${children}</${node.type}>`;
            } else {
                return children;
            }
        }
        //console.log(JSON.parse(JSON.stringify(node)));
        return recursive(node);*/
        //console.log(JSON.parse(JSON.stringify(node)));
        const build = (node: DeserializedNode, rules: DeserializedRules<HTMLElement>): JSX.Element|ReactElement|null|undefined|string => {
            if (
                !Element.isElement(node)
            ) {
                node = node as DeserializedLeaf;
                let leaf: JSX.Element|ReactElement|null|undefined|string = node.text;
                let leafKeys = Object.keys(rules.leaves);
                for (let i = 0; i < leafKeys.length; i++) {
                    let next = rules.leaves[leafKeys[i]].serialize(node, leaf, children => children?.map((child) => build(child, rules)))
                    if (next !== undefined) {
                        leaf = next;
                    }
                }
                return leaf;
            } else {
                node = node as DeserializedElement;
                let elementKeys = Object.keys(rules.elements);
                for (let i = 0; i < elementKeys.length; i++) {
                    let serialized = rules.elements[elementKeys[i]].serialize(node, children => children?.map((child) => build(child, rules)))
                    if (serialized !== undefined) {
                        return serialized;
                    }
                }
            }
            return null;
        }
        const flat = function (arr: any[], depth: number): any[] {

            // If depth is 0, return the array as-is
            if (depth < 1) {
                return arr.slice();
            }

            // Otherwise, concatenate into the parent array
            return arr.reduce(function (acc, val) {
                return acc.concat(Array.isArray(val) ? flat(val, depth - 1) : val);
            }, []);

        };
        const body = flat(node.map((node, key) => {
            return build(node, rules);
        }), 1000000).map((comp, key) => {
            if (comp && typeof comp !== "string" && !Array.isArray(comp)) {
                comp = React.cloneElement(comp, {key});
            }
            return comp;
        });
        //console.log(body);
        let html = renderToStaticMarkup(<body>{body}</body>);
        const parsed = new DOMParser().parseFromString(html, 'text/xml');
        html = parsed.getElementsByTagName("body")[0].innerHTML;
        /*const c = html.replace(/\s+/g, '');
        if (c === "<p/>" || c === "<p></p>") {
            if (props.value === undefined) {
                html = undefined;
            } else {
                html = null;
            }
        }*/
        //console.log(html);
        return html;
    }
}

const importFromHtml = (html: string|DataTransfer, rules: DeserializedRules<HTMLElement>) => htmlserializer.deserialize(html, rules);

const exportToHtml = (value: DeserializedElement[], rules: DeserializedRules<HTMLElement>) => htmlserializer.serialize(value, rules);

const PureElementRule = (Tag: string, {
    aliases = [],
    tagType = 'block',
    render,
    normalize,
    attributes = false,
}: {
    aliases?: string[],
    tagType?: 'block'|'inline',
    render?: (props: {editor: Editor, node: RenderElement}) => JSX.Element|undefined,
    normalize?: (args: {entry: NodeEntry, normalizeNode: (entry: NodeEntry) => void, editor: Editor}) => void,
    attributes?: boolean,
} = {}
): DeserializedElementRule<HTMLElement> => {
    return {
        isVoid: () => false,
        tagType: () => tagType,
        match: (element) => element.type === Tag,
        normalize,
        deserialize(el, next) {
            const name = el.tagName?.toLowerCase();
            if (name === Tag || aliases.indexOf(name) !== -1) {

                const parseStyles = (styles: string) => styles
                    .split(';')
                    .filter(style => style.split(':')[0] && style.split(':')[1])
                    .map(style => [
                        style.split(':')[0].trim().replace(/-./g, c => c.substr(1).toUpperCase()),
                        style.split(':').slice(1).join(':').trim()
                    ])
                    .reduce((styleObj, style) => ({
                        ...styleObj,
                        [style[0]]: style[1],
                    }), {});

                const formatPureAttributes = (attrs: { [key: string]: any }) => {
                    attrs = attrs || {};
                    if (attrs.class) {
                        attrs.className = attrs.class;//.trim().split(/\s+/).join(" ");
                        delete attrs.class;
                    }
                    if (attrs.cellpadding) {
                        attrs.cellPadding = attrs.cellpadding;
                        delete attrs.cellpadding;
                    }
                    if (attrs.cellspacing) {
                        attrs.cellSpacing = attrs.cellspacing;
                        delete attrs.cellspacing;
                    }
                    if (attrs.style && typeof attrs.style === "string") {
                        attrs.style = parseStyles(attrs.style);
                    }
                    return attrs;
                }

                const getAttributes = (el: HTMLElement): { [key: string]: any } => {
                    return Array.from(el.attributes)
                        .map(a => [a.name, a.value])
                        .reduce((acc, attr) => {
                            // @ts-ignore
                            acc[attr[0]] = attr[1]
                            return acc
                        }, {})
                }

                return {
                    type: Tag,
                    props: {},
                    attr: attributes ? formatPureAttributes(getAttributes(el)) : {},
                    children: next(el.childNodes)
                } as DeserializedElement;
            }
        },
        serialize(node, next) {
            if (node.type === Tag) {
                return <Tag {...node.attr}>{next(node.children)}</Tag>;
            }
        },
        render: (props): JSX.Element|undefined => {
            const {node} = props;
            return (node.element?.type === Tag ? (render ? render(props) : <Tag {...node.element.attr} {...node.attributes}>{node.children}</Tag>) : undefined);
        }
    }
}

const PureToggleableElementRule = ({onTag, offTag, hotKey, toolbarButton}: {onTag?: string, offTag?: string, hotKey?: HotKeyType, toolbarButton?: JSX.Element|ReactElement|null|undefined} = {}): DeserializedElementRule<HTMLElement> => {
    const isElementActive = (editor: Editor) => {
        // @ts-ignore
        const [match] = Editor.nodes(editor, {
            match: n =>
                !Editor.isEditor(n) &&
                SlateElement.isElement(n) &&
                // @ts-ignore
                n.type === onTag,
        });

        return !!match
    };
    return {
        ...PureElementRule(onTag),
        isActive: isElementActive,
        hotKey: hotKeyAction(hotKey),
        toolbarButton,
        toggle: (editor) => {
            const isActive = isElementActive(editor)
            //const { selection } = editor

            if (!isActive) {
                Transforms.setNodes(
                    editor,
                    // @ts-ignore
                    {type: onTag},
                    {
                        match: n =>
                            !Editor.isEditor(n) &&
                            SlateElement.isElement(n) &&
                            n.type === offTag,
                        split: true
                    });
            } else {
                // @ts-ignore
                Transforms.setNodes(editor, {type: offTag}, {
                    split: true
                })
            }
        }
    }
}

const PureLeafRule = (Tag: string, {aliases = []}: {aliases?: string[]} = {}): DeserializedLeafRule<HTMLElement> => {
    return {
        isVoid: () => false,
        tagType: () => 'inline',
        // @ts-ignore
        match: (element) => element[Tag] === true,
        deserialize(el, next) {
            const name = el.tagName?.toLowerCase();
            if (name === Tag || aliases.indexOf(name) !== -1) {
                return {
                    [Tag]: true,
                    text: el.textContent
                } as DeserializedLeaf;
            }
        },
        serialize(node, leaf, next) {
            if (node[Tag] === true) {
                // @ts-ignore
                return <Tag>{leaf}</Tag>;
            }
        },
        render: ({node, leaf}) => (node.leaf?.[Tag] !== undefined ? <Tag>{leaf}</Tag> : undefined)
    }
}

const PureToggleableLeafRule = ({leaf, aliases = [], hotKey, toolbarButton}: {leaf?: string, aliases?: string[], hotKey?: HotKeyType, toolbarButton?: JSX.Element|ReactElement|ReactComponentLike|null|undefined} = {}): DeserializedLeafRule<HTMLElement> => {
    const isLeafActive = (editor: Editor) => {
        const marks = Editor.marks(editor);
        // @ts-ignore
        return marks ? marks[leaf] === true : false
    }
    return {
        ...PureLeafRule(leaf, {aliases}),
        isActive: isLeafActive,
        hotKey: hotKeyAction(hotKey),
        toolbarButton,
        toggle: (editor) => {
            const isActive = isLeafActive(editor);

            if (isActive) {
                Editor.removeMark(editor, leaf)
            } else {
                Editor.addMark(editor, leaf, true)
            }

            /*if (!isActive) {
                Editor.addMark(editor, leaf, true);
            } else {
                Editor.removeMark(editor, leaf);
            }*/
            /*Transforms.setNodes(
                editor,
                { [leaf]: isActive ? undefined : true },
                { match: n => {
                    const s = Text.isText(n) && (isActive ? !!n[leaf] : true);
                    if (s) {
                        console.log(n);
                    }
                    return s;
                }, split: true }
            )*/
        }
    }
}

export {
    importFromHtml,
    exportToHtml,
    PureToggleableElementRule,
    PureToggleableLeafRule,
    PureElementRule,
    PureLeafRule
};